import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  ReactNode,
} from "react";

interface MidiContextType {
  midiAccess: WebMidi.MIDIAccess | null;
  noteHash: Record<number, boolean>;
  requestMIDIAccess: () => Promise<void>;
  isMIDISupported: boolean;
}

// Provide a default context value that matches the expected structure
const defaultMidiContext: MidiContextType = {
  midiAccess: null,
  noteHash: {},
  requestMIDIAccess: async () => {},
  isMIDISupported: false,
};

// Create a context with a default empty value.
const MidiContext = createContext<MidiContextType>(defaultMidiContext);

// Custom hook to use the context
export const useMidi = () => {
  const context = useContext(MidiContext);
  if (context === undefined) {
    throw new Error("useMidi must be used within a MidiProvider");
  }
  return context;
};

interface MidiProviderProps {
  children: ReactNode;
}

export const MidiProvider: React.FC<MidiProviderProps> = ({ children }) => {
  const [midiAccess, setMidiAccess] = useState<WebMidi.MIDIAccess | null>(null);
  const [noteHash, setNoteHash] = useState<Record<number, boolean>>({});
  const isMIDISupported = typeof navigator !== 'undefined' && !!navigator.requestMIDIAccess;

  const onMIDIMessage = (message: WebMidi.MIDIMessageEvent) => {
    const [status, note, velocity] = Array.from(message.data);
    const isNoteOnMessage = status === 144 && velocity > 0;
    const isNoteOffMessage =
      status === 128 || (status === 144 && velocity === 0);

    if (isNoteOnMessage || isNoteOffMessage) {
      setNoteHash((prevNoteHash) => {
        return { ...prevNoteHash, [note]: isNoteOnMessage };
      });
    }
  };

  const attachInputHandlers = (midiAccess: WebMidi.MIDIAccess) => {
    midiAccess.inputs.forEach((input) => {
      input.onmidimessage = onMIDIMessage;
      input.open(); // Explicitly open the input (important for iOS/iPad)
    });
  };

  const requestMIDIAccess = async () => {
    if (!isMIDISupported) {
      console.warn("WebMIDI is not supported in this browser!");
      alert("WebMIDI is not supported in this browser. Please try Chrome or Safari on iPad.");
      return;
    }

    try {
      // iOS/iPad requires explicit sysex option (set to false since we don't need it)
      const access = await navigator.requestMIDIAccess({ sysex: false });

      setMidiAccess(access);
      attachInputHandlers(access);

      // Listen for state changes (when MIDI devices are connected/disconnected)
      access.onstatechange = (event: WebMidi.MIDIConnectionEvent) => {
        if (event.port.type === 'input' && event.port.state === 'connected') {
          const input = event.port as WebMidi.MIDIInput;
          input.onmidimessage = onMIDIMessage;
          input.open();
        }
      };
    } catch (error) {
      const err = error as DOMException;
      console.error("Could not access your MIDI devices:", err);
      console.error("Error name:", err.name);
      console.error("Error message:", err.message);
      alert(`MIDI access denied: ${err.message}. Please check your browser permissions.`);
    }
  };

  // Try to request MIDI access automatically on mount (may not work on iOS/iPad)
  useEffect(() => {
    if (isMIDISupported) {
      // Try automatically, but don't show errors - user can use button if needed
      requestMIDIAccess().catch(() => {});
    }
  }, []);

  const contextValue = {
    noteHash,
    midiAccess,
    requestMIDIAccess,
    isMIDISupported,
  };

  return (
    <MidiContext.Provider value={contextValue}>{children}</MidiContext.Provider>
  );
};
